import { useEffect, useState } from 'react'

import { TruckMap } from './components/TruckMap'
import { sendToParent, usePostMessage } from './hooks/usePostMessage'
import { parseToken } from './services/auth'
import { fetchSegments } from './services/segments'

import type { SegmentFilters } from './services/segments'
import type { MapToken, TripSegment } from './types'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

export default function App() {
	const [token, setToken] = useState<MapToken | null>(null)
	const [allSegments, setAllSegments] = useState<TripSegment[]>([])
	const [filteredTripIds, setFilteredTripIds] = useState<string[] | null>(null)
	const [rawToken, setRawToken] = useState<string>('')
	const [useCustomIcon, setUseCustomIcon] = useState(false)
	const [urlFilters, setUrlFilters] = useState<SegmentFilters>({})
	const [selectedTripId, setSelectedTripId] = useState<string | null>(null)
	const [fitAllKey, setFitAllKey] = useState(0)

	// Computed: segments to display (filtered by parent or all)
	const displaySegments =
		filteredTripIds !== null
			? allSegments.filter((s) => {
					// Case-insensitive matching to handle potential case mismatches
					const normalizedTripId = s.trip_id.toLowerCase()
					const hasMatch = filteredTripIds.some((id) => id.toLowerCase() === normalizedTripId)
					return hasMatch
				})
			: allSegments

	// Debug: Log when displaySegments changes
	useEffect(() => {
		console.log('🗺️  Map displaying:', {
			totalSegments: allSegments.length,
			filteredSegments: displaySegments.length,
			isFiltered: filteredTripIds !== null,
			displayedTripIds: displaySegments.map((s) => s.trip_id),
		})
	}, [displaySegments, allSegments, filteredTripIds])

	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const tokenParam = params.get('token')
		const customIcon = params.get('customIcon')

		if (tokenParam) {
			try {
				setRawToken(tokenParam)
				setToken(parseToken(tokenParam))
			} catch (err) {
				console.error('Invalid token:', err)
			}
		}

		// Read filter parameters from URL
		const filters: SegmentFilters = {}
		const dateRange = params.get('date_range')
		const keyword = params.get('keyword')
		const orderStatus = params.get('order_status')
		const shipperIds = params.get('shipper_ids')
		const consigneeIds = params.get('consignee_ids')
		const isActive = params.get('is_active')
		const limitParam = params.get('limit')

		if (dateRange) filters.date_range = dateRange
		if (keyword) filters.keyword = keyword
		if (orderStatus) filters.order_status = orderStatus
		if (shipperIds) filters.shipper_ids = shipperIds
		if (consigneeIds) filters.consignee_ids = consigneeIds
		if (isActive) filters.is_active = isActive
		// Default to 1000 if not specified (matching PRO numbers endpoint default)
		filters.limit = limitParam ? parseInt(limitParam, 10) : 1000

		setUrlFilters(filters)
		console.log('📋 URL Filters:', filters)

		// Enable custom icon via URL parameter: ?customIcon=true
		if (customIcon === 'true') {
			setUseCustomIcon(true)
			console.log('✅ Custom icon enabled')
		}
	}, [])

	useEffect(() => {
		if (token && rawToken) {
			console.log('🔄 Fetching segments with filters:', urlFilters)
			fetchSegments(rawToken, API_URL, urlFilters)
				.then((segments) => {
					console.log(`✅ Fetched ${segments.length} segments`, {
						filters: urlFilters,
						activeCount: segments.filter((s) => s.is_active).length,
						inactiveCount: segments.filter((s) => !s.is_active).length,
					})
					setAllSegments(segments)

					// Send PRO numbers to parent (Bubble) for filtering
					const proNumbers = [...new Set(segments.map((s) => s.pro_num))]
					sendToParent({ type: 'proNumbers', pro_numbers: proNumbers })
					console.log(`📋 Sent ${proNumbers.length} PRO numbers to parent`)
				})
				.catch(console.error)
		}
	}, [token, rawToken, urlFilters])

	usePostMessage((msg) => {
		if (msg.type === 'setTrips') {
			console.log('🔍 Parent set trip filter:', {
				receivedCount: msg.trip_ids.length,
				receivedIds: msg.trip_ids,
			})

			// Debug: show available trip IDs for comparison
			if (msg.trip_ids.length > 0 && allSegments.length > 0) {
				const availableIds = allSegments.map((s) => s.trip_id)
				console.log('📍 Available trip IDs in segments:', availableIds)

				// Check for case-insensitive matches
				const matched = msg.trip_ids.filter((requestedId) =>
					allSegments.some((s) => s.trip_id.toLowerCase() === requestedId.toLowerCase())
				)
				const notMatched = msg.trip_ids.filter(
					(requestedId) =>
						!allSegments.some((s) => s.trip_id.toLowerCase() === requestedId.toLowerCase())
				)

				console.log('✅ Matched trip IDs:', matched.length, matched)
				if (notMatched.length > 0) {
					console.warn('⚠️ Trip IDs not found in segments:', notMatched)
				}
			}

			setFilteredTripIds(msg.trip_ids.length > 0 ? msg.trip_ids : null)
		}
		if (msg.type === 'setDateRange' && rawToken) {
			const dateRangeFilter = `${msg.startDate},${msg.endDate}`
			fetchSegments(rawToken, API_URL, {
				...urlFilters,
				date_range: dateRangeFilter,
			})
				.then(setAllSegments)
				.catch(console.error)
		}
		if (msg.type === 'fitAll') {
			setFitAllKey((n) => n + 1)
		}
		if (msg.type === 'highlight') {
			console.log('🔦 Highlight requested for:', msg.trip_id)

			// If null is sent, clear selection and zoom out
			if (msg.trip_id === null) {
				setSelectedTripId(null)
				setFitAllKey((n) => n + 1)
				return
			}

			setSelectedTripId(msg.trip_id)
		}
		if (msg.type === 'updateSegment') {
			setAllSegments((prev) => {
				const idx = prev.findIndex((s) => s.trip_id === msg.segment.trip_id)
				if (idx >= 0) {
					const updated = [...prev]
					updated[idx] = msg.segment
					return updated
				}
				return [...prev, msg.segment]
			})
		}
	})

	if (!token) return <div>Loading...</div>

	return (
		<TruckMap
			segments={displaySegments}
			mapboxToken={MAPBOX_TOKEN}
			styleURL={token.styleURL}
			useCustomIcon={useCustomIcon}
			token={rawToken}
			apiURL={API_URL}
			selectedTripId={selectedTripId}
			onTripSelect={setSelectedTripId}
			fitAllKey={fitAllKey}
		/>
	)
}
